import { type ContextProviderComponent, createContext } from "solid-js";
import type { SetStoreFunction } from "solid-js/store";
import { createStore } from "solid-js/store";
import { makePersisted } from "@solid-primitives/storage";
import type { InferOutput } from "valibot";
import { array, boolean, object, optional, parse, record, string } from "valibot";
import { useUserState } from "./user";
import { initializedContext } from "../utils/initializedUtil";
import { customerReference, folderReference, formReference } from "../utils/validation/idReferences";
import logger from "../utils/logger";

const Schema = object({
  lastCustomerId: optional(customerReference()),
  lastFolderId: optional(record(string(), folderReference())),
  lastEditedForms: optional(record(string(), array(formReference()))),
  globalSearch: optional(
    object({
      recentlySearched: record(string(), array(string())),
    }),
  ),
  dismissedBanners: optional(array(string())),
  dismissedNewPlatformTour: optional(boolean()),
});
type State = InferOutput<typeof Schema>;

type Dispatch = SetStoreFunction<State>;
const StateContext = createContext<State>();
const DispatchContext = createContext<Dispatch>();

export const LocalUserSettingsProvider: ContextProviderComponent<undefined> = (props) => {
  const user = useUserState();
  // oxlint-disable-next-line solid/reactivity -- False-positive, 'makePersisted' retains reactivity
  const [state, setState] = makePersisted<State, [State, SetStoreFunction<State>]>(createStore<State>({}), {
    name: `moreapp_local_user_settings_${user().id}`,
    storage: localStorage,
    serialize: (value) => {
      try {
        const parsed = parse(Schema, value);
        return JSON.stringify(parsed);
      } catch (e) {
        throw new Error("Failed to serialize local user settings", { cause: e });
      }
    },
    deserialize: (value) => {
      try {
        const json = JSON.parse(value);
        return parse(Schema, json);
      } catch (e) {
        logger.error("Failed to deserialize local user settings", e);
        return {};
      }
    },
  });

  return (
    <StateContext.Provider value={state}>
      <DispatchContext.Provider value={setState}>{props.children}</DispatchContext.Provider>
    </StateContext.Provider>
  );
};

export const useLocalUserSettings = () =>
  initializedContext(StateContext, "useLocalUserSettings", "LocalUserSettingsProvider");
export const useLocalUserSettingsDispatch = () =>
  initializedContext(DispatchContext, "useLocalUserSettingsDispatch", "LocalUserSettingsProvider");
